import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useSetAtom } from 'jotai/react'
import useSWR from 'swr'
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation'

import { HttpCode } from '@/constants/common'
import { tokenMapAtom } from '@/stores/services'

import { Result } from './types'
import { useToken } from './useLoginWallet'

export enum Method {
  GET = 'GET',
  POST = 'POST'
}

export const errorRetryCount = 3
export const refreshInterval = 3 * 1000

const abortStack = new Map<string, AbortController>()

export const fetcher = async ([url, method, data, config]: [
  url: string | null,
  Method,
  any,
  RequestInit | undefined
]) => {
  if (!url) return

  let body: BodyInit | undefined
  url = import.meta.env.VITE_API_BASE_URL + url
  if (import.meta.env.VITE_MODE === 'production')
    url = import.meta.env.VITE_API_TARGET_URL + url

  if (method === Method.GET && data) url += `?${new URLSearchParams(data)}`
  else if (data instanceof FormData) body = data
  else body = JSON.stringify(data)

  // 检查未返回的重复请求
  if (abortStack.get(url)) {
    abortStack.get(url)?.abort()
    abortStack.delete(url)
  }
  const controller = new AbortController()
  abortStack.set(url, controller)

  const res = await fetch(url, {
    body,
    method,
    ...config,
    signal: controller.signal
  })
  // 请求结束，删除
  abortStack.delete(url)

  if (!res.ok) {
    const error = new Error('An error occurred while fetching the data.')
    // 将额外的信息附加到错误对象上。
    // @ts-ignore
    error.info = await res.json()
    // @ts-ignore
    error.status = res.status
    throw error
  }

  return res.json()
}

function useFetch<D = any, P = any>(
  url: string | null,
  method: Method,
  params?: P,
  callback?: {
    onSuccess?: (data: D, code?: Result['code']) => void
    onError?: (error: Error) => void
  },
  config?: RequestInit,
  options?: Parameters<typeof useSWR<Result<D>>>[2]
) {
  const { t } = useTranslation()
  const { token, address } = useToken()
  const setTokenMap = useSetAtom(tokenMapAtom)
  const isFormData = params instanceof FormData
  const [polling, setPolling] = useState(true) // 轮询状态，默认为开启

  const { data, error, isLoading, isValidating, ...rest } = useSWR<Result<D>>(
    [
      url,
      method,
      params,
      {
        ...config,
        headers: {
          ...(!isFormData && { 'Content-Type': 'application/json' }),
          ...(token && { Authorization: `Bearer ${token}` }),
          ...config?.headers
        }
      }
    ],
    fetcher,
    {
      // https://swr.vercel.app/zh-CN/docs/api
      errorRetryCount,
      revalidateOnFocus: false,
      dedupingInterval: 4000,
      ...options,
      refreshInterval:
        polling && options?.refreshInterval
          ? options.refreshInterval
          : undefined,
      onError: (err) => {
        // console.log(`>>>>>> useFetch onError: ${url} `, err)
        callback?.onError?.(err)
      },
      onSuccess(data) {
        if (url === null) return
        // console.log(`>>>>>> useFetch onSuccess: ${url} `, data)
        callback?.onSuccess?.(data.data, data.code)
      }
    }
  )

  useEffect(() => {
    if (
      data &&
      [
        HttpCode.SignatureExpired,
        HttpCode.AuthorizationExpires,
        HttpCode.TokenExpired
      ].includes(data.code)
    ) {
      setTokenMap((prev) => ({
        ...prev,
        [address!]: ''
      }))
      toast.error(t('common.LoginExpired'))
    }
  }, [address, data, setTokenMap, t])

  useEffect(() => {
    if (isLoading || isValidating) {
      setPolling(false)
    } else {
      setPolling(true)
    }
  }, [isLoading, isValidating])

  return {
    data: data?.data,
    error,
    isLoading,
    isValidating,
    ...rest
  }
}

export default useFetch

// 手动调用接口
export function useMutation({
  url,
  method = Method.GET,
  config,
  payload,
  options
}: {
  url: string
  method?: Method
  payload?: any
  config?: RequestInit
  options?: SWRMutationConfiguration<any, any, string, any, any>
}) {
  const { t } = useTranslation()
  const { token, address } = useToken()
  const setTokenMap = useSetAtom(tokenMapAtom)

  const { data, error, isMutating, ...rest } = useSWRMutation(
    url,
    (url: string, { arg }: { arg: any }): Promise<any> => {
      const isFormData = arg instanceof FormData
      return fetcher([
        url,
        method || Method.GET,
        {
          ...payload,
          ...arg
        },
        {
          ...config,
          headers: {
            ...(!isFormData && { 'Content-Type': 'application/json' }),
            ...(token && { Authorization: `Bearer ${token}` }),
            ...config?.headers
          }
        }
      ]).then((resp) => {
        if (resp?.code == 0 || resp?.code == 200) {
          return resp
        } else {
          return Promise.reject(resp)
        }
      })
    },
    {
      ...options
    }
  )

  useEffect(() => {
    if (
      data &&
      [
        HttpCode.SignatureExpired,
        HttpCode.AuthorizationExpires,
        HttpCode.TokenExpired
      ].includes(data.code)
    ) {
      setTokenMap((prev) => ({
        ...prev,
        [address!]: ''
      }))
      toast.error(t('common.LoginExpired'))
    }
  }, [address, data, setTokenMap, t])

  return {
    data: data?.data,
    error,
    isMutating,
    ...rest
  }
}
